import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { validateSignUpForm, hasFormErrors } from '../../utils/validation';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';
import type { SignUpFormData, FormErrors } from '../../types/auth';

interface SignUpFormProps {
  onSuccess?: () => void;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess }) => {
  const { signUp, loading, error, clearError } = useAuth();
  const [formData, setFormData] = useState<SignUpFormData>({ email: '', password: '', confirmPassword: '' });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (formErrors[name as keyof FormErrors]) setFormErrors(p => ({ ...p, [name]: undefined }));
    if (error) clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateSignUpForm(formData);
    setFormErrors(errors);
    if (hasFormErrors(errors)) return;
    const { error: signUpError } = await signUp(formData.email, formData.password);
    if (!signUpError) {
      setIsSuccess(true);
      if (onSuccess) onSuccess();
    }
  };

  if (isSuccess) {
    return (
      <div style={{ textAlign: 'center', padding: '1rem 0' }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1.25rem',
        }}>
          <CheckCircle size={26} color="#10b981" strokeWidth={2} />
        </div>
        <h2 style={{ fontSize: '1.375rem', fontWeight: 800, color: '#f1f5f9', marginBottom: '0.625rem', letterSpacing: '-0.03em' }}>
          Check Your Email
        </h2>
        <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1.5rem', lineHeight: 1.65 }}>
          We sent a confirmation link to{' '}
          <span style={{ color: '#94a3b8', fontWeight: 600 }}>{formData.email}</span>.
          Click it to activate your account.
        </p>
        <Link
          to="/auth/login"
          style={{
            display: 'inline-flex', alignItems: 'center',
            background: '#3b82f6', color: '#fff',
            padding: '0.625rem 1.25rem', borderRadius: 9,
            fontWeight: 600, fontSize: '0.875rem',
          }}
        >
          Go to Sign In
        </Link>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em', marginBottom: '0.375rem' }}>
          Create account
        </h2>
        <p style={{ fontSize: '0.875rem', color: '#475569' }}>
          Join Nitrextrader — free, no credit card needed
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <Input
          label="Email Address"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          error={formErrors.email}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />

        <div style={{ position: 'relative' }}>
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleChange}
            error={formErrors.password}
            placeholder="Create a strong password"
            required
            autoComplete="new-password"
            helperText="Min 8 chars with uppercase, lowercase, number and special character"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            style={{ position: 'absolute', right: 10, top: 30, background: 'none', border: 'none', cursor: 'pointer', color: '#334155', padding: 2, display: 'flex', alignItems: 'center', transition: 'color 0.18s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#94a3b8'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#334155'; }}
          >
            {showPassword ? <EyeOff size={16} strokeWidth={2} /> : <Eye size={16} strokeWidth={2} />}
          </button>
        </div>

        <div style={{ position: 'relative' }}>
          <Input
            label="Confirm Password"
            type={showConfirm ? 'text' : 'password'}
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={formErrors.confirmPassword}
            placeholder="Confirm your password"
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            aria-label={showConfirm ? 'Hide password' : 'Show password'}
            style={{ position: 'absolute', right: 10, top: 30, background: 'none', border: 'none', cursor: 'pointer', color: '#334155', padding: 2, display: 'flex', alignItems: 'center', transition: 'color 0.18s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#94a3b8'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#334155'; }}
          >
            {showConfirm ? <EyeOff size={16} strokeWidth={2} /> : <Eye size={16} strokeWidth={2} />}
          </button>
        </div>

        {error && (
          <div
            role="alert"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '0.75rem 0.875rem', fontSize: '0.8125rem', color: '#f87171' }}
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={loading}
          style={{ width: '100%', marginTop: '0.25rem', borderRadius: 9 }}
        >
          {loading ? 'Creating account…' : 'Create Account'}
        </Button>
      </form>

      <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
        <p style={{ fontSize: '0.8125rem', color: '#334155', marginBottom: '0.75rem' }}>
          Already have an account?{' '}
          <Link
            to="/auth/login"
            style={{ color: '#3b82f6', fontWeight: 600, transition: 'color 0.18s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#60a5fa'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#3b82f6'; }}
          >
            Sign in
          </Link>
        </p>
        <p style={{ fontSize: '0.72rem', color: '#1e3a5f', lineHeight: 1.55 }}>
          By creating an account you agree to our{' '}
          <a href="/terms" style={{ color: '#1e3a5f', textDecoration: 'underline' }}>Terms</a>
          {' '}and{' '}
          <a href="/privacy" style={{ color: '#1e3a5f', textDecoration: 'underline' }}>Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};
